import React from "react";
import { Link } from "react-router-dom";
import {
  Strategy,
  Megaphone,
  Heart,
  ChartLineUp,
  Coins,
  Database,
  ArrowUpRight,
  ArrowRight,
} from "@phosphor-icons/react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const SERVICES = [
  {
    num: "01",
    icon: Strategy,
    title: "Strategic HR Business Partnering",
    tag: "HRBP",
    body: "Embedded thinking partners for founders, CEOs and CHROs. We translate business goals into people bets — org design, workforce planning, leadership alignment.",
    deliverables: [
      "Business-linked people plan",
      "Org design & span-of-control",
      "Leadership team diagnostics",
      "Quarterly HR operating cadence",
    ],
  },
  {
    num: "02",
    icon: Megaphone,
    title: "Talent Acquisition & Employer Brand",
    tag: "Hiring",
    body: "Hiring engines for velocity and signal. Outbound sourcing, intake rituals, hiring scorecards, and a narrative that punches above your funding round.",
    deliverables: [
      "Intake & interview kit redesign",
      "Sourcing channel strategy",
      "Hiring dashboards & SLAs",
      "EVP & employer brand narrative",
    ],
  },
  {
    num: "03",
    icon: Heart,
    title: "Retention, Engagement & Culture",
    tag: "Culture",
    body: "Culture as an operating system. Stay interviews, listening systems, manager loops, ritual design — calibrated for post-funding turbulence.",
    deliverables: [
      "Attrition diagnostic",
      "Listening system (eNPS, pulse, stay)",
      "Manager rhythm & 1:1 system",
      "Culture operating cadence",
    ],
  },
  {
    num: "04",
    icon: ChartLineUp,
    title: "Performance & Capability",
    tag: "Perform",
    body: "From PMS theatre to performance enablement. Goal architecture, review cadence, calibration, manager skills, and L&D that moves the business.",
    deliverables: [
      "Goal architecture & OKR design",
      "Review & calibration system",
      "Manager enablement curriculum",
      "L&D architecture (roles × skills)",
    ],
  },
  {
    num: "05",
    icon: Coins,
    title: "Compensation, Benefits & Rewards",
    tag: "Rewards",
    body: "Pay philosophy, band design, equity refreshers, variable pay, recognition — benchmarked against Bangalore's repriced AI and product talent market.",
    deliverables: [
      "Pay philosophy & band design",
      "Equity & refresher policy",
      "Variable pay & STI structures",
      "Benefits & recognition design",
    ],
  },
  {
    num: "06",
    icon: Database,
    title: "HR Operations, Data & Compliance",
    tag: "Ops",
    body: "The unsexy foundation that keeps a growing company audit-ready and analytics-ready. POSH, statutory, HRIS, dashboards, policy refresh.",
    deliverables: [
      "HRIS selection & rollout",
      "Policy refresh (POSH, statutory)",
      "People dashboards & data hygiene",
      "Compliance & audit readiness",
    ],
  },
];

function PageHero() {
  return (
    <section className="relative pt-32 md:pt-40 pb-16 grid-bg overflow-hidden">
      <div className="absolute inset-0 beam pointer-events-none" />
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-6 rise d-1">
          / Services
        </div>
        <h1
          data-testid="services-headline"
          className="font-display text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.95] text-white max-w-4xl rise d-2"
        >
          Six modules.
          <span className="block text-cy">One operating system.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-zinc-400 text-base md:text-lg rise d-3">
          Every company faces the same people questions in a different order. Our services are
          modular — you choose the entry point, we install the underlying operating cadence.
        </p>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="bg-[#050505] text-white min-h-screen">
      <Header />
      <main>
        <PageHero />

        <section className="bg-[#070709] border-y border-ink-600">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-16 md:py-24">
            <div className="grid gap-0 border-t border-l border-ink-500">
              {SERVICES.map((s) => (
                <div
                  key={s.num}
                  data-testid={`service-${s.num}`}
                  className="group grid grid-cols-12 gap-0 border-r border-b border-ink-500 bg-[#050505] hover:bg-[#0B0B10] transition-colors"
                >
                  <div className="col-span-12 md:col-span-1 p-6 md:p-8 border-b md:border-b-0 md:border-r border-ink-500 flex md:flex-col items-center md:items-start justify-between md:justify-start gap-4">
                    <span className="font-mono text-xs text-cy">/ {s.num}</span>
                    <s.icon size={28} weight="duotone" className="text-cy" />
                  </div>
                  <div className="col-span-12 md:col-span-5 p-6 md:p-8 md:border-r border-ink-500">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3">
                      {s.tag} module
                    </div>
                    <h2 className="font-display text-xl md:text-2xl font-bold text-white tracking-tight leading-snug">
                      {s.title}
                    </h2>
                    <p className="mt-4 text-sm md:text-base text-zinc-400 leading-relaxed max-w-lg">
                      {s.body}
                    </p>
                  </div>
                  <div className="col-span-12 md:col-span-5 p-6 md:p-8 md:border-r border-ink-500">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">
                      Deliverables
                    </div>
                    <ul className="space-y-2">
                      {s.deliverables.map((d) => (
                        <li key={d} className="flex items-center gap-3 text-sm text-zinc-300">
                          <span className="w-1.5 h-1.5 bg-cy inline-block rotate-45" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="col-span-12 md:col-span-1 flex items-center justify-end md:justify-center p-6 md:p-8">
                    <ArrowUpRight
                      size={22}
                      weight="bold"
                      className="text-zinc-600 group-hover:text-cy transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-cy text-ink">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-tight">
              Not sure which module to start with?
            </h2>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="inline-flex items-center gap-3 bg-ink text-cy px-8 py-4 text-sm font-semibold hover:bg-white hover:text-ink transition-colors"
            >
              Talk to a partner <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
